/**
 * The Proposal tab in the quote builder — `docs/proposal.md` §10.
 *
 * **The customer's page on the left and the controls on the right**, and the
 * left is the *same* `<Proposal>` the portal renders (§2 rule 3). Nothing here
 * is a second rendering: a "preview" assembled from the same data by different
 * components is a preview that will one day disagree with what was sent, and
 * the disagreement will be found by a customer.
 *
 * The preview is live and **cannot sign**. Selections made in it are kept in
 * memory and thrown away; the Approve card is replaced by a grey line saying
 * signing is off, which is honest about what the frame is. Approving from the
 * office is the bar's **Approve** (folder 52 §5, Board 3 A) — the typed-name
 * approve that used to sit here is gone, and on a sent quote the line says
 * so: *Karen signs on the link. To record a yes yourself, use Approve.*
 *
 * **What is on the rail is PR 2's four cards**: the message and how long it is
 * good for, Brand (read-only), Terms (read-only) and Before you send. The
 * *What the customer sees* card — detail, which kinds of line, crew hours,
 * the price view and the timing mode — is PR 3, which is where the things it
 * changes are drawn. `PATCH /quotes/:id/proposal` already stores all eight
 * fields and `renderable()` on the server clamps the two this release cannot
 * draw, so the order is a UI decision rather than a schema one: a control
 * that visibly changes nothing is worse than one that arrives with its
 * effect.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  PROPOSAL_KINDS, RESPONSE_LABELS,
  type ProposalKind, type ProposalSelection, type ProposalView, type QuoteCheck,
  type QuoteResponse,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { formatDocumentDate, formatDollarsExact, formatRelative } from '../lib/format.js'
import { Button, Segmented, Switch, Tag } from '../components/primitives.js'
import { SettingsCard } from '../components/settings-card.js'
import { TextArea, NumberInput } from '../components/form/index.js'
import { IconCheck, IconEye, IconFile, IconLock, IconSend, IconWarn } from '../frame/icons.js'
import { Proposal, type ProposalLayout } from './proposal/index.js'
import { ApprovalRow } from './approval-row.js'
import { Lockup } from './proposal/top-bar.js'
import { brandSkin } from './proposal/brand.js'

/** The three widths the segmented control offers, in px (§10.1). */
const FRAMES = { desktop: 1280, ipad: 820, phone: 390 } as const
type FrameKind = keyof typeof FRAMES

/**
 * Which layout each frame draws — the one `Proposal` itself would pick at
 * that window width (`RAIL_FROM`), said here because a frame inside the
 * builder's window cannot ask a media query.
 */
const LAYOUTS: Record<FrameKind, ProposalLayout> = {
  desktop: 'desktop', ipad: 'tablet', phone: 'phone',
}

export interface ProposalTabProps {
  quoteId: string
  view: ProposalView
  selection: ProposalSelection
  onSelection: (selection: ProposalSelection) => void
  /** `quotes: edit` at the quote's branch. A viewer gets a read-only rail. */
  canEdit: boolean
  /**
   * `quotes: edit`, whatever the quote's state: what the customer sent arrives after the quote
   * went out, so *Mark handled* can't follow the draft-only `canEdit` (walkthrough Oct 30).
   */
  canHandle?: boolean
  /**
   * The preview's sign area on a quote that is with the customer (folder 52
   * §8 A, §14): *Karen signs on the link. To record a yes yourself, use
   * Approve.* Absent, the line says signing is off, as before.
   */
  signNote?: string | null
  busy?: boolean
  error?: string | null
  /** Re-read the proposal after a rail save, so the preview follows the edit. */
  onChanged: (view: ProposalView) => void
  phone: boolean
}

export function ProposalTab(props: ProposalTabProps) {
  const { quoteId, view, selection, onSelection, signNote, busy, error } = props
  const [frame, setFrame] = useState<FrameKind>('desktop')

  /*
   * On a phone the rail goes under the preview rather than beside it: 372px
   * of controls and a 390px page do not sit side by side, and the estimator
   * reading this on a phone is checking it rather than writing it.
   */
  if (props.phone) {
    return (
      <div className="flex flex-col gap-5 px-4">
        <PreviewFrame frame="phone" onFrame={setFrame} quoteId={quoteId} view={view} phone>
          <Proposal
            view={view}
            selection={selection}
            onSelection={onSelection}
            busy={busy}
            error={error}
            layout="phone"
          />
        </PreviewFrame>
        <ProposalRail {...props} />
      </div>
    )
  }

  return (
    <div className="flex items-start gap-5 px-5">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <PreviewFrame frame={frame} onFrame={setFrame} quoteId={quoteId} view={view}>
          <Proposal
            view={view}
            selection={selection}
            onSelection={onSelection}
            busy={busy}
            error={error}
            /*
             * Each frame draws the layout a real screen of its width gets
             * (Aaron's review, item 2.6): the 820 iPad is the one column and
             * the bottom bar, never the desktop with its rail squeezed into
             * 820px — which is what `compact={frame === 'phone'}` drew, since
             * anything that was not a phone was a desktop.
             */
            layout={LAYOUTS[frame]}
          />
        </PreviewFrame>
        <p className="m-0 rounded-field bg-surface-alt px-3 py-2 text-[12.5px] text-text-muted"
          data-sign-area="">
          {signNote ?? 'Preview — signing is off. Send the quote to let the customer approve it.'}
        </p>
      </div>
      <ProposalRail {...props} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The frame
// ---------------------------------------------------------------------------

/**
 * The page at a real device width, scaled to fit the column.
 *
 * `transform: scale()` on a box of the *real* width, rather than a column
 * that happens to be narrow: a 390px-wide `div` inside a 900px column is not
 * a phone — its media queries read the window — so the page would lay itself
 * out for a desktop and be drawn small. Scaling a real 390px render is the
 * only way the preview is the page, and `compact` tells the page which
 * layout to use rather than leaving it to a query about the wrong box.
 *
 * The scale is measured with a `ResizeObserver` because the column's width
 * depends on the rail, the window and the nav, and reading it once on mount
 * gets it wrong the first time the window moves.
 */
function PreviewFrame({ frame, onFrame, quoteId, view, phone, children }: {
  frame: FrameKind
  onFrame: (frame: FrameKind) => void
  quoteId: string
  view: ProposalView
  phone?: boolean
  children: React.ReactNode
}) {
  const box = useRef<HTMLDivElement | null>(null)
  const page = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)
  const [height, setHeight] = useState(0)
  const [sent, setSent] = useState<string | null>(null)
  const width = FRAMES[frame]

  useLayoutEffect(() => {
    const element = box.current
    if (!element || typeof ResizeObserver !== 'function') return undefined
    const measure = () => setScale(Math.min(1, element.clientWidth / width))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [width])

  /*
   * The scaled page still occupies its unscaled height in the layout, which
   * leaves a column of empty page under a phone preview about three times as
   * tall as the preview. So the wrapper is told what the render actually
   * measures and multiplies it by the scale.
   */
  useLayoutEffect(() => {
    const element = page.current
    if (!element || typeof ResizeObserver !== 'function') return undefined
    const measure = () => setHeight(element.scrollHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [frame, view])

  const sendTest = useCallback(async () => {
    setSent(null)
    try {
      const body = await request<{ delivered: boolean; reason?: string; to: string }>(
        `/quotes/${quoteId}/proposal/test`, { method: 'POST', body: {} })
      setSent(body.delivered
        ? `Sent to ${body.to}.`
        : `That could not be emailed: ${body.reason ?? 'no mailbox is connected yet'}.`)
    } catch (cause) {
      setSent(cause instanceof Error ? cause.message : 'That could not be sent.')
    }
  }, [quoteId])

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="whitespace-nowrap text-[13.5px] font-bold text-text">Customer view</span>
        {!phone && (
          <span className="w-[250px] shrink-0">
            <Segmented
              label="Preview width"
              size="field"
              full
              value={frame}
              onChange={onFrame}
              options={[
                { value: 'desktop', label: 'Desktop' },
                { value: 'ipad', label: 'iPad' },
                { value: 'phone', label: 'Phone' },
              ]}
            />
          </span>
        )}
        {/*
          * Absent under `2xl`, rather than truncated.
          *
          * It is an explanation, not a control, and the row it sits in holds
          * three controls that must not give up their width for it. Clipped
          * to "This is the page your custo…" it says less than nothing — the
          * reader learns only that something was cut.
          */}
        <span className="min-w-0 flex-1 text-[12px] leading-[1.35] text-text-muted
                         max-2xl:hidden">
          {`This is the page ${firstNameOf(view.preparedFor)} opens from the text message. `
            + 'It is the same page, not a copy.'}
        </span>
        <span className="flex-1 2xl:hidden" />
        <Button
          className="h-[30px] shrink-0 text-[12.5px]"
          title="Sends to you only"
          onClick={() => void sendTest()}
        >
          <IconSend size={15} /> Send me a test
        </Button>
        <a
          href={`/quotes/${quoteId}/preview`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-button border
                     border-line bg-surface px-2.5 text-[12.5px] font-semibold text-text
                     no-underline hover:border-line-strong"
        >
          <IconEye size={15} /> Open as the customer
        </a>
      </div>

      {sent && (
        <p className="m-0 text-[12.5px] text-text-muted">{sent}</p>
      )}

      {/*
        * A browser window around the page (board 10): the address bar is what
        * says "this is what they open", which a bordered box does not. The
        * window is the device's width, never wider, so a phone preview is a
        * phone in the middle of the column rather than a strip down its left.
        */}
      <div className="flex justify-center">
        <div
          style={{ maxWidth: width + 2 }}
          className="w-full min-w-0 overflow-hidden rounded-record border border-line bg-surface
                     shadow-popover"
        >
          <div className="flex h-9 items-center gap-3 border-b border-line bg-surface-alt px-3.5">
            <span className="inline-flex shrink-0 gap-1.5" aria-hidden>
              <i className="h-2.5 w-2.5 rounded-full bg-line-strong" />
              <i className="h-2.5 w-2.5 rounded-full bg-line-strong" />
              <i className="h-2.5 w-2.5 rounded-full bg-line-strong" />
            </span>
            <span className="inline-flex h-[22px] min-w-0 flex-1 items-center gap-1.5 rounded-[6px]
                             border border-line bg-surface px-2.5 text-[11.5px] text-text-muted">
              <IconLock size={11} />
              <span className="truncate">{portalAddress(view.quoteId)}</span>
            </span>
          </div>
          <div ref={box} style={{ height: height * scale }} className="overflow-hidden">
            <div
              ref={page}
              style={{ width, transform: `scale(${scale})`, transformOrigin: 'top left' }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** "rowted.app/portal/quote/…" — where the customer's link lands, said as an address. */
function portalAddress(quoteId: string): string {
  const host = typeof window === 'undefined' ? '' : window.location.host
  return `${host}/portal/quote/${quoteId}`
}

/** "Dana" out of "Dana & Marcus Okafor"; the customer when there is no name. */
function firstNameOf(name: string | null | undefined): string {
  const first = name?.trim().split(/\s+/)[0]
  return first || 'your customer'
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

/**
 * The four cards.
 *
 * **Past draft it is read-only** (§10.1): changing the message or the wording
 * under a customer who is reading the page would change a document they are
 * about to sign. The card says what to do instead rather than greying out
 * without a reason.
 */
function ProposalRail(props: ProposalTabProps) {
  const { quoteId, view, canEdit, onChanged } = props
  const office = view.office
  const locked = office?.locked ?? false
  const editable = canEdit && !locked

  const [message, setMessage] = useState(view.message)
  const [days, setDays] = useState(office?.validDays ?? 30)
  const [saved, setSaved] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  /* The server is the truth: a re-read after a save replaces the draft. */
  useEffect(() => { setMessage(view.message) }, [view.message])
  useEffect(() => { if (office) setDays(office.validDays) }, [office?.validDays])

  const save = useCallback(async (patch: Record<string, unknown>) => {
    setFailed(null)
    try {
      const body = await request<{ proposal: ProposalView }>(
        `/quotes/${quoteId}/proposal`, { method: 'PATCH', body: patch })
      onChanged(body.proposal)
      setSaved(new Date().toISOString())
    } catch (cause) {
      setFailed(cause instanceof Error ? cause.message : 'That did not save.')
    }
  }, [quoteId, onChanged])

  /*
   * Saved on a debounce as you type (§10.1), 800ms, and only for a value that
   * differs from the one the server last sent — otherwise the effect saves
   * once on every re-read it causes.
   */
  useEffect(() => {
    if (!editable || message === view.message) return undefined
    const timer = window.setTimeout(() => { void save({ message }) }, 800)
    return () => window.clearTimeout(timer)
  }, [message, view.message, editable, save])

  useEffect(() => {
    if (!editable || !office || days === office.validDays) return undefined
    if (!Number.isFinite(days) || days < 1 || days > 365) return undefined
    const timer = window.setTimeout(() => { void save({ validDays: days }) }, 800)
    return () => window.clearTimeout(timer)
  }, [days, office?.validDays, editable, office, save])

  const skin = brandSkin(view.identity)

  return (
    <aside className="flex w-[372px] shrink-0 flex-col gap-3.5 max-lg:w-full">
      {/* After the office approved: who, how, who recorded it and what was said (folder 52 §8 A). */}
      {office?.approval && <ApprovalRow approval={office.approval} />}
      {locked && (
        <p className="m-0 rounded-field border border-line bg-surface-alt px-3 py-2
                      text-[12.5px] leading-[1.45] text-text-muted">
          This quote has been sent. Revise and resend to change the message or the
          wording — a customer may be reading the page right now.
        </p>
      )}
      {failed && <p className="m-0 text-[12.5px] text-danger">{failed}</p>}

      <SettingsCard
        title="Message to the customer"
        sub={saved ? 'saved' : undefined}
      >
        <div className="flex flex-col gap-3 px-[18px] py-3.5">
          {editable
            ? (
              <>
                <TextArea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={5}
                  maxLength={600}
                  placeholder="Dana, thank you for walking the property with us on Tuesday…"
                />
                <span className="self-end text-[11.5px] text-text-faint">
                  {`${message.length} / 600`}
                </span>
              </>
            )
            : (
              <p className="m-0 whitespace-pre-wrap text-[13px] leading-[1.5] text-text-body">
                {message || 'No message. The intro card shows the three facts only.'}
              </p>
            )}

          <div className="flex items-center gap-2.5 border-t border-line-faint pt-3">
            <span className="text-[13px] text-text-body">Good for</span>
            {editable
              ? (
                <span className="w-[72px]">
                  <NumberInput
                    value={String(days)}
                    onChange={(event) => {
                      const typed = Number.parseInt(event.target.value, 10)
                      setDays(Number.isFinite(typed) ? typed : 0)
                    }}
                  />
                </span>
              )
              : <b className="text-[13px] font-bold text-text">{days}</b>}
            <span className="text-[13px] text-text-body">days</span>
            {/*
              * The date is computed on the day it is *sent*, so a draft says
              * what the window is rather than naming a day that will have
              * moved by the time anybody reads it.
              */}
            <span className="ml-auto text-[12px] text-text-muted">
              {view.expiresOn
                ? `expires ${formatDocumentDate(view.expiresOn)}`
                : 'counted from the day you send it'}
            </span>
          </div>
        </div>
      </SettingsCard>

      <SeenCard view={view} editable={editable} onSave={save} />

      <RecommendedCard view={view} quoteId={quoteId} editable={editable} onChanged={onChanged} />

      <BrandCard view={view} />

      <TermsCard view={view} quoteId={quoteId} />

      {/*
        * **From the customer** (§10.3), at the top of nothing and the bottom
        * of the rail on purpose: it is only drawn when there is something to
        * read, and a card that appears above the settings would push the
        * whole rail down every time somebody asked a question.
        */}
      <FromTheCustomer quoteId={view.quoteId} canEdit={props.canHandle ?? editable} />

      <ChecksCard checks={office?.checks ?? []} statusLine={statusLine(view)} />

      {/* Kept mounted so the palette is read even where no card draws it. */}
      <span className="hidden" style={skin.vars} aria-hidden />
    </aside>
  )
}

/**
 * What the customer sees — §10.1 card 2.
 *
 * Five controls, in the order somebody reaches for them: how far a visit
 * opens, which kinds of line are listed, whether labor says its hours, how
 * the money reads, and how the timing reads. Each saves as it is changed and
 * the preview beside it redraws, which is the whole argument for putting the
 * controls next to the page rather than in a dialog over it.
 *
 * **It starts from Settings and applies to this quote only** — the footer
 * says so, because a company that changes a default and sees last week's
 * quote unchanged will otherwise file it as a bug. The copy at creation is
 * the point (§10): a sent document must never change under the person
 * reading it.
 */
function SeenCard({ view, editable, onSave }: {
  view: ProposalView
  editable: boolean
  onSave: (patch: Record<string, unknown>) => Promise<void>
}) {
  const settings = view.settings
  const allKinds = PROPOSAL_KINDS.every((kind) => settings.kinds.includes(kind))

  return (
    <SettingsCard title="What the customer sees">
      <RailRow
        label="Detail"
        help="Everything opens each service to its materials, labor and equipment.
              Choices always show."
      >
        <Segmented
          label="Detail"
          size="field"
          full
          value={settings.detail}
          onChange={(detail) => { void onSave({ detail }) }}
          options={[
            { value: 'visits', label: 'Visits' },
            { value: 'services', label: 'Services' },
            { value: 'lines', label: 'Everything' },
          ]}
        />
      </RailRow>

      <RailRow
        label="Lines to list"
        help="Fees read as an upcharge, so they start hidden. A hidden line is still in
              the price."
      >
        <div className="flex flex-wrap gap-1.5">
          {PROPOSAL_KINDS.map((kind) => {
            const on = settings.kinds.includes(kind)
            return (
              <button
                key={kind}
                type="button"
                disabled={!editable}
                aria-pressed={on}
                onClick={() => { void onSave({
                  kinds: on
                    ? settings.kinds.filter((one) => one !== kind)
                    : [...settings.kinds, kind],
                }) }}
                className={cn('inline-flex h-7 items-center gap-[5px] rounded-chip border px-2.5',
                  'text-[12px] font-semibold',
                  on
                    ? 'border-accent-line bg-accent-soft text-accent-dark'
                    : 'border-dashed border-line-strong bg-surface text-text-faint')}
              >
                {on && <IconCheck size={11} />}
                {KIND_WORDS[kind]}
              </button>
            )
          })}
        </div>
      </RailRow>

      <RailRow
        inline
        label="Show a price on every line"
        help={allKinds
          ? 'Each line carries its own figure.'
          : 'Available once all four kinds are listed — otherwise the lines would not '
            + 'add up to the service above them.'}
      >
        <Switch
          checked={settings.linePrices}
          disabled={!editable || !allKinds}
          label="Show a price on every line"
          hideLabel
          onChange={(linePrices) => { void onSave({ linePrices }) }}
        />
      </RailRow>

      <RailRow
        inline
        label="Show crew hours"
        help="Labor says how many hours. Off shows the labor line without them."
      >
        <Switch
          checked={settings.showHours}
          disabled={!editable}
          label="Show crew hours"
          hideLabel
          onChange={(showHours) => { void onSave({ showHours }) }}
        />
      </RailRow>

      {/*
       * Aaron, step 26 (0130): the number on a material or fee line, which
       * the projection leaves out of the JSON when this is off — the same
       * bargain Show crew hours makes for labor.
       */}
      <RailRow
        inline
        label="Show material and fee quantities"
        help="Materials and fees say how much. Off shows those lines without the number."
      >
        <Switch
          checked={settings.showQuantities}
          disabled={!editable}
          label="Show material and fee quantities"
          hideLabel
          onChange={(showQuantities) => { void onSave({ showQuantities }) }}
        />
      </RailRow>

      <RailRow label="Price shown as" help={priceLine(view)}>
        <Segmented
          label="Price shown as"
          size="field"
          full
          value={settings.priceView}
          onChange={(priceView) => { void onSave({ priceView }) }}
          options={[
            { value: 'plan', label: 'Billing plan' },
            { value: 'visit', label: 'Per visit' },
            { value: 'both', label: 'Both' },
          ]}
        />
      </RailRow>

      <RailRow
        label="When the visits happen"
        help={timingLine(view)}
      >
        <Segmented
          label="When the visits happen"
          size="field"
          full
          value={settings.timing}
          onChange={(timing) => { void onSave({ timing }) }}
          /*
           * **Dates is absent, not greyed, where the company has not allowed
           * it** (§10.4): it is a company decision about what an estimator
           * may promise, and a segment that looks on and is not is a control
           * somebody will press twice and then report.
           */
          options={[
            { value: 'count', label: 'Count' },
            { value: 'frequency', label: 'Weekly' },
            { value: 'season', label: 'Season' },
            ...(settings.datesAllowed
              ? [{ value: 'dates' as const, label: 'Dates' }]
              : []),
          ]}
        />
      </RailRow>

      <div className="flex flex-col gap-1.5 px-[18px] py-3">
        <span className="flex items-center gap-1.5 text-[11.5px] leading-[1.4] text-text-muted">
          <span className="flex-1">
            Starts from your Settings. Changes here apply to this quote only.
          </span>
          <a href="/settings/proposal"
            className="whitespace-nowrap text-[12px] font-semibold text-accent no-underline
                       hover:text-accent-dark">
            Settings →
          </a>
        </span>
        {/*
          * The fixed footnote, and it is fixed because it is the one promise
          * none of these controls can break: §4.3's projection is what makes
          * it true, and saying it here is what stops somebody looking for the
          * switch that would turn it off.
          */}
        <span className="text-[11.5px] leading-[1.45] text-text-muted">
          Costs, margins, internal notes and crew notes never appear, whatever is
          switched on here.
        </span>
      </div>
    </SettingsCard>
  )
}

/**
 * A live line from *this* quote, so "Billing plan" is not an abstraction.
 *
 * "Follows how this work bills: $288 a month for the lawn care" is the
 * difference between a setting somebody understands and one they try and
 * undo. Built from the visits the view already carries, so it cannot
 * disagree with the preview two inches to its left.
 */
function priceLine(view: ProposalView): React.ReactNode {
  const period = view.visits.find((visit) => visit.price.plan.unit !== null)
  if (!period) return 'How a recurring visit’s money reads.'
  const unit = period.price.plan.unit ?? 'month'
  return (
    <>
      Follows how this work bills:{' '}
      <b className="font-semibold text-text">
        {`${formatDollarsExact(period.price.plan.figure)} a ${unit}`}
      </b>
      {` for ${period.name.toLowerCase()}.`}
    </>
  )
}

/**
 * What the timing control is saying on *this* quote — "Season says Weekly,
 * October to early December." — then what Dates would mean, or that the
 * company has them off. Read off the visits the preview is drawing, like
 * `priceLine`, so the sentence and the page cannot disagree.
 */
function timingLine(view: ProposalView): string {
  const tail = view.settings.datesAllowed
    ? 'Dates only when you have them to promise.'
    : 'Dates are off for the whole company.'
  if (view.settings.timing !== 'season') return tail
  const seasonal = view.visits.find((visit) => visit.timing.season)
  if (!seasonal?.timing.season) return tail
  const said = [seasonal.timing.frequency, seasonal.timing.season.label]
    .filter(Boolean).join(', ')
  return `Season says ${said}. ${tail}`
}

/**
 * One control on the rail (board 10): the label, the control the full width
 * of the card under it, and the sentence under that. A switch sits **inline**
 * instead, at the right of its label, because a 34px switch alone on a 340px
 * line reads as something that fell off the row above.
 */
function RailRow({ label, help, inline = false, children }: {
  label: string
  help: React.ReactNode
  inline?: boolean
  children: React.ReactNode
}) {
  if (inline) {
    return (
      <div className="flex items-start gap-3 border-b border-line-faint px-[18px] py-2.5
                      last:border-b-0">
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <b className="text-[12.5px] font-bold text-text">{label}</b>
          <span className="text-[11.5px] leading-[1.4] text-text-muted">{help}</span>
        </div>
        <div className="shrink-0 pt-0.5">{children}</div>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1.5 border-b border-line-faint px-[18px] pb-2.5 pt-3
                    last:border-b-0">
      <span className="text-[12.5px] font-semibold text-text">{label}</span>
      {children}
      <span className="text-[11.5px] leading-[1.4] text-text-muted">{help}</span>
    </div>
  )
}

const KIND_WORDS: Record<ProposalKind, string> = {
  material: 'Materials', labor: 'Labor', equipment: 'Equipment', fee: 'Fees',
}

/**
 * Brand — read-only, and it says whether we changed anything (§10.1 card 4).
 *
 * A brand guardian who sees a slightly different green on a proposal and is
 * not told why will file it as a bug. So the card draws the lockup the
 * customer will see, the colour as it will be used, and `brandNote()`'s
 * sentence — which names the number and says the logo is untouched.
 */
function BrandCard({ view }: { view: ProposalView }) {
  const skin = brandSkin(view.identity)
  const brand = view.office?.brand
  const adjusted = Boolean(brand && (brand.darkened || brand.nearWhite))
  const hex = view.identity.brandColor?.toUpperCase() ?? null
  return (
    <SettingsCard title="Brand">
      <div style={skin.vars} className="flex flex-col gap-2.5 px-[18px] py-3.5">
        <div className="flex items-center gap-3">
          <span className="inline-flex min-w-0 items-center gap-2.5">
            <Lockup identity={view.identity} size={34} />
            {!view.identity.logoUrl && (
              <b className="truncate text-[14px] font-bold leading-[1.05] tracking-[-0.015em]
                            text-text">
                {view.identity.name}
              </b>
            )}
          </span>
          <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-[12px]
                           text-text-body">
            <i className="inline-block h-[18px] w-[18px] rounded-[5px] bg-brand
                          shadow-[inset_0_0_0_1px_rgb(0_0_0/0.12)]" />
            {hex ?? 'Default green'}
          </span>
        </div>
        <CheckLine level={adjusted ? 'warn' : 'ok'}
          label={brand?.note || 'No colour saved, so the page uses the default green.'} />
        <a
          href="/settings/profile"
          className="text-[12px] font-semibold text-accent no-underline hover:text-accent-dark"
        >
          Change logo and colour in Settings · Company →
        </a>
      </div>
    </SettingsCard>
  )
}

/**
 * Terms — read-only (board 10). The picker stays where it already lives, on
 * the Build tab's rail, and **Change** goes there: a second chooser here would
 * be two controls for one field.
 */
function TermsCard({ view, quoteId }: { view: ProposalView; quoteId: string }) {
  const terms = view.terms
  const sections = view.office?.terms.sections ?? terms?.sections.length ?? 0
  const updated = terms?.updatedOn ? monthYear(terms.updatedOn) : null
  return (
    <SettingsCard title="Terms of service">
      <div className="flex flex-col gap-2.5 px-[18px] py-3.5">
        <div className="flex items-center gap-2.5">
          <span className={cn('inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center',
            'rounded-button', terms ? 'bg-accent-soft text-accent' : 'bg-warn-bg text-warn')}>
            <IconFile size={16} />
          </span>
          <div className="flex min-w-0 flex-col">
            <b className="truncate text-[12.5px] font-bold text-text">
              {terms ? `${terms.name} · version ${terms.revision}` : 'No terms attached'}
            </b>
            <span className="text-[11.5px] text-text-muted">
              {terms
                ? [updated ? `Updated ${updated}` : '',
                  sections === 1 ? '1 section' : `${sections} sections`]
                  .filter(Boolean).join(' · ')
                : 'The signature agrees to nothing written down.'}
            </span>
          </div>
          <a href={`/quotes/${quoteId}?tab=build`}
            className="ml-auto shrink-0 text-[12px] font-semibold text-accent no-underline
                       hover:text-accent-dark">
            Change
          </a>
        </div>
        <span className="text-[11.5px] leading-[1.45] text-text-muted">
          {`Edit the sections in Catalog › Terms. Whatever ${firstNameOf(view.preparedFor)}`
            + ' signs is saved with the signature.'}
        </span>
      </div>
    </SettingsCard>
  )
}

/** "Mar 2026" — how recent a set of terms is, without a day nobody needs. */
function monthYear(day: string): string {
  const [year, month] = day.slice(0, 10).split('-').map(Number)
  if (!year || !month) return day
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${names[month - 1]} ${year}`
}

/**
 * Recommended pick (board 10): which answer to each question the customer
 * opens on, badged. One per question, and pressing the chosen one again
 * clears it — "leave it empty and nothing is preselected" is a real answer.
 * The server clears the previous one in the same transaction, so the tick
 * moves rather than doubling.
 */
function RecommendedCard({ view, quoteId, editable, onChanged }: {
  view: ProposalView
  quoteId: string
  editable: boolean
  onChanged: (view: ProposalView) => void
}) {
  const [failed, setFailed] = useState<string | null>(null)
  if (view.groups.length === 0) return null

  const pick = async (optionId: string, recommended: boolean) => {
    setFailed(null)
    try {
      await request(`/quotes/${quoteId}/options/${optionId}`,
        { method: 'PATCH', body: { recommended } })
      const body = await request<{ proposal: ProposalView }>(`/quotes/${quoteId}/proposal`)
      onChanged(body.proposal)
    } catch (cause) {
      setFailed(cause instanceof Error ? cause.message : 'That did not save.')
    }
  }

  return (
    <SettingsCard title="Recommended pick">
      <div className="flex flex-col gap-3 px-[18px] py-3.5">
        {view.groups.map((group) => (
          <div key={group.id} role="radiogroup" aria-label={group.name}
            className="flex flex-col gap-1.5">
            <span className="text-[12.5px] text-text">
              <b className="font-bold">{group.name}</b> · choose one
            </span>
            <div className="flex flex-col gap-[5px]">
              {group.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={option.recommended}
                  disabled={!editable}
                  onClick={() => { void pick(option.id, !option.recommended) }}
                  className={cn('flex items-center gap-2 rounded-button border px-2.5 py-[7px]',
                    'text-left text-[12.5px] text-text disabled:cursor-default',
                    option.recommended
                      ? 'border-accent-line bg-green-band-bg'
                      : 'border-line bg-surface hover:border-line-strong')}
                >
                  <i className={cn('inline-block h-3.5 w-3.5 shrink-0 rounded-full',
                    option.recommended
                      ? 'border-4 border-accent bg-surface'
                      : 'border-[1.5px] border-line-strong')} />
                  <span className="truncate">{option.name}</span>
                  {option.recommended && <Tag tone="ok" className="ml-1.5">Recommended</Tag>}
                  <span className="tnum ml-auto font-bold">
                    {formatDollarsExact(option.price.total)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
        {failed && <span className="text-[12px] text-danger">{failed}</span>}
        <span className="text-[11.5px] leading-[1.45] text-text-muted">
          Preselected for the customer with a Recommended badge. They can still pick the
          other. Leave it empty and nothing is preselected.
        </span>
      </div>
    </SettingsCard>
  )
}

/**
 * **From the customer** — `docs/proposal.md` §10.3.
 *
 * Unhandled responses only: a question somebody has dealt with is history,
 * and the Activity tab is where history belongs. The words are shown in full
 * rather than truncated — this is the one surface that exists to read them —
 * and **Mark handled** is a column, not a delete, because what a customer
 * wrote about a document they were about to sign is evidence (rule 2).
 *
 * It fetches its own rows rather than riding on `view.office`, because a
 * response arrives while somebody is looking at the page and a card that only
 * updated when the whole proposal was re-read would be a card that is wrong
 * most of the time somebody needs it.
 */
export function FromTheCustomer({ quoteId, canEdit }: { quoteId: string; canEdit: boolean }) {
  const [rows, setRows] = useState<QuoteResponse[]>([])

  const load = useCallback(() => {
    void request<{ responses: QuoteResponse[] }>(`/quotes/${quoteId}/responses`)
      .then((payload) => setRows(payload.responses))
      /* A rail card that cannot load is a card that is not there. */
      .catch(() => { /* not worth a word */ })
  }, [quoteId])

  useEffect(load, [load])

  const open = rows.filter((one) => one.handledAt === null)
  if (open.length === 0) return null

  const handle = (id: string) => {
    setRows((was) => was.map((one) => (one.id === id
      ? { ...one, handledAt: new Date().toISOString() }
      : one)))
    void request(`/quotes/${quoteId}/responses/${id}`,
      { method: 'PATCH', body: { handled: true } })
      .catch(load)
  }

  return (
    <SettingsCard title="From the customer">
      {open.map((one) => (
        <div key={one.id}
          className="flex flex-col gap-1.5 border-b border-line-faint px-[18px] py-3
                     last:border-b-0">
          <div className="flex items-baseline gap-2">
            <span className={cn(`rounded-chip px-1.5 py-px text-[10.5px] font-semibold`,
              one.kind === 'decline'
                ? 'bg-danger-bg text-danger'
                : 'bg-accent-soft text-accent-dark')}>
              {RESPONSE_LABELS[one.kind]}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-text-muted">
              {one.contactName ?? 'A customer'}
            </span>
            <span className="shrink-0 text-[11.5px] text-text-faint">
              {formatRelative(one.createdAt)}
            </span>
          </div>

          {one.reason && (
            <p className="m-0 text-[12.5px] font-semibold text-text">{one.reason}</p>
          )}
          {one.body && (
            <p className="m-0 whitespace-pre-wrap text-[12.5px] leading-[1.45] text-text-body">
              {one.body}
            </p>
          )}
          {one.refs.length > 0 && (
            <p className="m-0 text-[11.5px] text-text-muted">
              About: {one.refs.map((ref) => ref.name).join(', ')}
            </p>
          )}

          {canEdit && (
            <button
              type="button"
              onClick={() => handle(one.id)}
              className="flex h-8 items-center self-start rounded-field px-1.5
                         text-[12px] font-semibold text-accent"
            >
              Mark handled
            </button>
          )}
        </div>
      ))}
    </SettingsCard>
  )
}

/**
 * Before you send — the quote's own checks, plus the proposal's.
 *
 * `info` is a grey dot and never blocks: "this visit shows its dates" is
 * something to know rather than something to fix, and a level the rail cannot
 * draw would be a check nobody reads.
 */
function ChecksCard({ checks, statusLine }: { checks: QuoteCheck[]; statusLine: string | null }) {
  return (
    <SettingsCard title="Before you send">
      <div className="flex flex-col gap-2 px-[18px] py-3.5">
        {statusLine && (
          <span className="text-[12.5px] leading-[1.4] text-text-muted">{statusLine}</span>
        )}
        {checks.length === 0 && !statusLine && <CheckLine level="ok" label="Nothing to flag." />}
        {checks.map((check) => (
          <CheckLine key={check.key} level={check.level} label={check.label} />
        ))}
      </div>
    </SettingsCard>
  )
}

/**
 * One check: an 18px tinted circle and the sentence (board 10). The circle's
 * colour is the level and its glyph says it again, so a row is never read by
 * colour alone; `info` is a grey dot rather than a triangle, because "no logo
 * is uploaded" wearing the same amber as "this visit has no price" is how both
 * stop being read.
 */
function CheckLine({ level, label }: { level: QuoteCheck['level']; label: string }) {
  return (
    <div className="flex items-start gap-2 text-[12.5px] leading-[1.4] text-text">
      <span className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center',
        'rounded-full',
        level === 'ok' ? 'bg-accent-soft text-accent'
          : level === 'warn' ? 'bg-warn-bg text-warn'
            : level === 'blocking' ? 'bg-danger-bg text-danger'
              : 'bg-line-soft text-text-faint')}>
        {level === 'ok'
          ? <IconCheck size={11} />
          : level === 'info'
            ? <i className="block h-1.5 w-1.5 rounded-full bg-current" />
            : <IconWarn size={11} />}
      </span>
      <span className="min-w-0 flex-1">{label}</span>
    </div>
  )
}

/** "Sent Oct 10 · Viewed Oct 10 · Not signed yet" (§10.1 card 6). */
function statusLine(view: ProposalView): string | null {
  const office = view.office
  if (!office || office.sentAt === null) return null
  const parts = [`Sent ${formatDocumentDate(office.sentAt)}`]
  if (office.viewedAt) parts.push(`Viewed ${formatDocumentDate(office.viewedAt)}`)
  parts.push(view.signed ? 'Signed' : 'Not signed yet')
  return parts.join(' · ')
}

